import { useState, useEffect, useRef } from 'react';
import { Icon as IconifyIcon } from '@iconify/react';
import { Icon, IconName } from '@/icons';
import { SidebarProps, DashboardTab } from '@/types/dashboard';
import { AnimatedCard } from '@/components/AnimatedElements';
import { useThemeColors } from '@/styles';
import { layout, spacing, sizing } from '@/styles/design-tokens';
import { useAuth } from '@/hooks/useAuth';
import { useActiveOrganization } from '@/hooks/useActiveOrganization';
import { UserRole } from '@/types/enums/UserRole';
import { OrgSwitcher } from './OrgSwitcher';
import packageJson from '../../../../package.json';

// Icons that loop natively via repeatCount="indefinite" — no JS restart needed.
const NATIVE_LOOP_ICONS = new Set([
  'line-md:cog-loop',
  'line-md:watch-loop',
  'line-md:speedometer-loop',
  'line-md:gauge-loop',
]);

// Restart interval (ms): animation duration + ~600ms pause before replaying.
// account-alert-loop is intentionally here — its native loop is a barely-visible
// stroke-width pulse; restarting the full draw animation is far more visible.
const RESTART_MS: Record<string, number> = {
  'line-md:grid-3': 1200,
  'line-md:person-search': 2400,
  'line-md:person-add': 2400,
  'line-md:home-md': 2000,
  'line-md:clipboard-list': 2200,
  'line-md:clipboard-check': 2000,
  'line-md:account-alert-loop': 2000,
};

function LoopingIcon({ icon }: { icon: string }) {
  const wrapperRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (NATIVE_LOOP_ICONS.has(icon)) return;
    const ms = RESTART_MS[icon];
    if (!ms) return;

    const timer = setInterval(() => {
      const svg = wrapperRef.current?.querySelector(
        'svg'
      ) as SVGSVGElement | null;
      svg?.setCurrentTime(0);
    }, ms);

    return () => clearInterval(timer);
  }, [icon]);

  return (
    <span
      ref={wrapperRef}
      className="flex items-center justify-center w-5 h-5 flex-shrink-0"
    >
      <IconifyIcon icon={icon} width={20} height={20} />
    </span>
  );
}

// Tabs that use the Lucide icon library instead of Iconify animated icons
const tabLucideIconMap: Partial<Record<string, IconName>> = {};

// Sidebar tab → animated icon mapping
const tabAnimatedIconMap: Record<string, string> = {
  [DashboardTab.MY_BOARD]: 'line-md:grid-3',
  [DashboardTab.PEOPLE]: 'line-md:person-search',
  [DashboardTab.LEARNING_PROGRESS]: 'line-md:speedometer-loop',
  [DashboardTab.MANAGE_PEOPLE]: 'line-md:person-add',
  [DashboardTab.ORGS_MANAGEMENT]: 'line-md:home-md',
  [DashboardTab.CONFIG_MANAGEMENT]: 'line-md:cog-loop',
  [DashboardTab.BOARD_TEMPLATES]: 'line-md:clipboard-list',
  [DashboardTab.SCHEDULED_JOBS]: 'line-md:watch-loop',
  [DashboardTab.SYSTEM_HEALTH]: 'line-md:gauge-loop',
  [DashboardTab.GROUPS]: 'line-md:account-alert-loop',
  [DashboardTab.COHORTS]: 'line-md:clipboard-check',
};

export default function Sidebar({
  isCollapsed,
  onToggle,
  activeTab,
  onTabChange,
  tabs,
}: SidebarProps) {
  const colors = useThemeColors();
  const { user } = useAuth();
  const { isViewingDifferentOrg } = useActiveOrganization();
  const [hoveredTab, setHoveredTab] = useState<string | null>(null);
  const version = packageJson.version;
  const isOpen = !isCollapsed;

  const isAdmin = user?.role === UserRole.ADMIN;
  const isMentor = user?.role === UserRole.MENTOR;
  const isSuperadminOrg = user?.organization?.is_superadmin_org === true;

  // Filter tabs based on user role and org context.
  //
  // User types:
  //   Student             — base tabs only
  //   Mentor              — base + cohorts
  //   Admin (normal org)  — base + cohorts + admin tabs
  //   Superadmin own org  — base + cohorts + admin tabs + superadmin tabs
  //   Superadmin other org— base + cohorts + admin tabs (no superadmin tabs)
  const visibleTabs = tabs.filter(tab => {
    switch (tab.id) {
      // Superadmin-only tabs — own org and must be ADMIN role
      case DashboardTab.ORGS_MANAGEMENT:
      case DashboardTab.SYSTEM_HEALTH:
        return isSuperadminOrg && isAdmin && !isViewingDifferentOrg;

      // Admin-only tabs (shown in any org the admin is viewing)
      case DashboardTab.MANAGE_PEOPLE:
      case DashboardTab.BOARD_TEMPLATES:
      case DashboardTab.CONFIG_MANAGEMENT:
      case DashboardTab.SCHEDULED_JOBS:
        return isAdmin;

      // Mentor + Admin tabs
      case DashboardTab.COHORTS:
        return isAdmin || isMentor;

      // All other tabs (MY_BOARD, PEOPLE, VELOCITY, GROUPS) — visible to every role
      default:
        return true;
    }
  });

  const handleTabClick = (tabId: string) => {
    onTabChange(tabId);
    onToggle(); // Auto-close drawer on tab selection
  };

  return (
    <>
      {/* Backdrop overlay — all screen sizes */}
      {isOpen && (
        <div
          className="fixed inset-0 z-20 bg-black bg-opacity-50"
          onClick={onToggle}
        />
      )}

      {/* Sidebar drawer */}
      <aside
        className={`fixed left-0 z-30 transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{
          top: layout.header.height,
          height: `calc(100vh - ${layout.header.height})`,
          width: layout.sidebar.width,
          backgroundColor: colors.background,
          borderRight: `1px solid ${colors.border.default}`,
        }}
      >
        <div className="flex flex-col h-full w-full">
          {/* Tab list */}
          <div className="flex-1 overflow-y-auto w-full min-h-0">
            <div
              className="flex flex-col"
              style={{
                gap: spacing.sm,
                paddingLeft: 'calc(20px * var(--sizing-multiplier, 1))',
                paddingRight: 'calc(20px * var(--sizing-multiplier, 1))',
                paddingTop: spacing.lg,
                paddingBottom: spacing.lg,
              }}
            >
              {visibleTabs.map(tab => {
                const isActive = activeTab === tab.id;
                const isHovered = hoveredTab === tab.id;

                return (
                  <AnimatedCard key={tab.id} hoverEffect="tilt">
                    <button
                      className="w-full flex items-center justify-start rounded-lg transition-all duration-200 px-3 hover:translate-x-1 shadow-soft hover:shadow-soft-md"
                      style={{
                        height: sizing.button.lg,
                        gap: spacing.md,
                        ...(isActive
                          ? {
                              backgroundColor: colors.active.background,
                              color: colors.active.text,
                            }
                          : isHovered
                            ? {
                                backgroundColor: colors.hover.background,
                                color: colors.hover.text,
                              }
                            : {
                                backgroundColor: 'transparent',
                                color: colors.text.primary,
                              }),
                      }}
                      onClick={() => handleTabClick(tab.id)}
                      onMouseEnter={() => setHoveredTab(tab.id)}
                      onMouseLeave={() => setHoveredTab(null)}
                    >
                      {tabLucideIconMap[tab.id] ? (
                        <span className="flex items-center justify-center w-5 h-5 flex-shrink-0">
                          <Icon name={tabLucideIconMap[tab.id]!} size="md" />
                        </span>
                      ) : tabAnimatedIconMap[tab.id] ? (
                        <LoopingIcon icon={tabAnimatedIconMap[tab.id]} />
                      ) : null}
                      <span className="font-medium">{tab.name}</span>
                    </button>
                  </AnimatedCard>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div
            className="flex-shrink-0 border-t"
            style={{ borderColor: colors.border.default }}
          >
            <div
              className="flex flex-col"
              style={{
                paddingLeft: 'calc(20px * var(--sizing-multiplier, 1))',
                paddingRight: 'calc(20px * var(--sizing-multiplier, 1))',
                paddingTop: spacing.md,
                paddingBottom: spacing.md,
                gap: spacing.sm,
              }}
            >
              <OrgSwitcher />
              <div
                className="text-xs text-center"
                style={{ color: colors.text.secondary }}
              >
                LearnBahn Dashboard v{version}
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
